import { coordKey, type Coord, type GameState } from "@dailywars/core/engine";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, type RefObject } from "react";

import { syncCanvasBackingStore } from "#/game/canvasBacking";
import {
  drawBuildingsLayer,
  drawDynamicLayers,
  drawTerrainAndRoads,
  isAnimationComplete,
  TILE_SIZE,
  type CanvasBoardAnimation,
} from "#/game/canvasRenderer";
import { collectOccupiedUnitTileKeys } from "#/game/canvasRenderer/boardUnitGeometry";
import { useGameStoreApi } from "#/game/GameStoreProvider";

import { boardRedrawScheduleInput, nextBoardRedrawDelayMs } from "./boardDrawLoopSchedule";
import { createBoardFrameLoop } from "./boardDrawLoopScheduler";
import type { CanvasDrawRefs } from "./boardDrawLoopTypes";
import { createCachedLayer, type CachedLayer } from "./boardLayerCache";
import type { GameCanvasViewState } from "./types";

type BoardRenderer = {
  requestFrame: () => void;
  dispose: () => void;
};

type BoardLayerCaches = {
  terrain: CachedLayer;
};

function maybeCompleteAnimation({
  drawRefs,
  animation,
  nowMs,
}: {
  drawRefs: CanvasDrawRefs;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): void {
  if (
    animation &&
    !drawRefs.hasCompletedAnimationRef.current &&
    isAnimationComplete(animation, nowMs)
  ) {
    drawRefs.hasCompletedAnimationRef.current = true;
    drawRefs.onAnimationCompleteRef.current();
  }
}

function paintBoard({
  ctx,
  drawRefs,
  animation,
  caches,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  drawRefs: CanvasDrawRefs;
  animation: CanvasBoardAnimation | null;
  caches: BoardLayerCaches;
  nowMs: number;
}): void {
  const state = drawRefs.stateRef.current;
  const canvas = ctx.canvas;
  const dpr = syncCanvasBackingStore({
    canvas,
    logicalWidth: state.width * TILE_SIZE,
    logicalHeight: state.height * TILE_SIZE,
  });
  const backingWidth = canvas.width;
  const backingHeight = canvas.height;
  const dims = { state, backingWidth, backingHeight, dpr };
  const terrainImage = caches.terrain.draw({ ...dims, key: null });
  const viewState = {
    ...drawRefs.viewStateRef.current,
    hoveredTileKey: drawRefs.hoveredTileKeyRef.current,
  };
  const occupiedTileKeys = collectOccupiedUnitTileKeys({ state, viewState, animation, nowMs });

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, backingWidth, backingHeight);
  if (terrainImage) ctx.drawImage(terrainImage, 0, 0);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawBuildingsLayer({ ctx, state, occupiedTileKeys, animation, nowMs });
  drawDynamicLayers({
    ctx,
    state,
    viewState,
    animation,
    nowMs,
  });

  maybeCompleteAnimation({ drawRefs, animation, nowMs });
}

function createBoardRenderer({
  ctx,
  drawRefs,
}: {
  ctx: CanvasRenderingContext2D;
  drawRefs: CanvasDrawRefs;
}): BoardRenderer {
  const caches: BoardLayerCaches = {
    terrain: createCachedLayer(drawTerrainAndRoads),
  };
  let activeAnimation: CanvasBoardAnimation | null = null;
  let activeStartMs = 0;

  const loop = createBoardFrameLoop({
    getDelayMs: (nowMs) => nextBoardRedrawDelayMs(boardRedrawScheduleInput({ drawRefs, nowMs })),
    paint: (nowMs) => {
      const rawAnimation = drawRefs.animationRef.current;
      if (rawAnimation !== activeAnimation) {
        activeAnimation = rawAnimation;
        activeStartMs = nowMs;
      }
      const animation = rawAnimation ? { ...rawAnimation, startedAtMs: activeStartMs } : null;
      paintBoard({ ctx, drawRefs, animation, caches, nowMs });
    },
  });

  return {
    requestFrame: loop.requestFrame,
    dispose: loop.dispose,
  };
}

function useCanvasDrawRefs({
  state,
  viewState,
  animation,
  onAnimationComplete,
}: {
  state: GameState;
  viewState: GameCanvasViewState;
  animation: CanvasBoardAnimation | null;
  onAnimationComplete: () => void;
}): CanvasDrawRefs {
  const hasCompletedAnimationRef = useRef(false);
  const stateRef = useRef(state);
  const viewStateRef = useRef(viewState);
  const hoveredTileKeyRef = useRef<string | null>(null);
  const animationRef = useRef(animation);
  const onAnimationCompleteRef = useRef(onAnimationComplete);

  useLayoutEffect(() => {
    stateRef.current = state;
    viewStateRef.current = viewState;
    animationRef.current = animation;
    onAnimationCompleteRef.current = onAnimationComplete;
  }, [state, viewState, animation, onAnimationComplete]);

  const animationSessionKey = animation ? `${animation.type}:${animation.startedAtMs}` : null;
  useEffect(() => {
    hasCompletedAnimationRef.current = false;
  }, [animationSessionKey]);

  return useMemo(
    () => ({
      stateRef,
      viewStateRef,
      hoveredTileKeyRef,
      animationRef,
      onAnimationCompleteRef,
      hasCompletedAnimationRef,
    }),
    [
      stateRef,
      viewStateRef,
      hoveredTileKeyRef,
      animationRef,
      onAnimationCompleteRef,
      hasCompletedAnimationRef,
    ],
  );
}

function useCanvasRenderer({
  canvasRef,
  drawRefs,
}: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  drawRefs: CanvasDrawRefs;
}): () => void {
  const rendererRef = useRef<BoardRenderer | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const renderer = createBoardRenderer({ ctx, drawRefs });
    rendererRef.current = renderer;
    renderer.requestFrame();
    return () => {
      renderer.dispose();
      rendererRef.current = null;
    };
  }, [canvasRef, drawRefs]);

  useEffect(() => {
    rendererRef.current?.requestFrame();
  });

  return useCallback(() => rendererRef.current?.requestFrame(), []);
}

function useHoveredTileKeySync({
  hoveredTileKeyRef,
  requestFrame,
}: {
  hoveredTileKeyRef: RefObject<string | null>;
  requestFrame: () => void;
}): void {
  const storeApi = useGameStoreApi();
  useEffect(() => {
    const push = (tile: Coord | null) => {
      const key = tile ? coordKey(tile) : null;
      if (hoveredTileKeyRef.current === key) return;
      hoveredTileKeyRef.current = key;
      requestFrame();
    };
    push(storeApi.getState().hoveredTile);
    return storeApi.subscribe((s, prev) => {
      if (s.hoveredTile !== prev.hoveredTile) push(s.hoveredTile);
    });
  }, [hoveredTileKeyRef, requestFrame, storeApi]);
}

export function useCanvasDrawLoop(props: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  state: GameState;
  viewState: GameCanvasViewState;
  animation: CanvasBoardAnimation | null;
  onAnimationComplete: () => void;
}): void {
  const drawRefs = useCanvasDrawRefs({
    state: props.state,
    viewState: props.viewState,
    animation: props.animation,
    onAnimationComplete: props.onAnimationComplete,
  });
  const requestFrame = useCanvasRenderer({
    canvasRef: props.canvasRef,
    drawRefs,
  });
  useHoveredTileKeySync({ hoveredTileKeyRef: drawRefs.hoveredTileKeyRef, requestFrame });
}
